import { Fragment, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useAppState } from "../../hooks/useAppState";
import { getVanillaEl } from "../../domBridge";
import { apiFetch } from "../../../api";
import { requestLock } from "../../../lockBus";
import { showToast } from "../../../toast";
import { confirmDialog } from "../../../dialogs";
import { notifyStateChange } from "../../../stateBus";
import { CommentItem } from "./CommentItem";
import { MentionInput } from "./MentionInput";
import type { AlbumComment } from "../../../types";

function CommentsBody() {
  const albumsDetailId = useAppState((s) => s.albumsDetailId);
  const albums = useAppState((s) => s.albums);
  const currentUserEmail = useAppState((s) => s.currentUserEmail);

  const album = albums.find((a) => a.id === albumsDetailId);
  const [comments, setComments] = useState<AlbumComment[]>(album?.comments || []);
  const [openReplyForId, setOpenReplyForId] = useState<string | null>(null);
  const [openEditForId, setOpenEditForId] = useState<string | null>(null);
  const [composerValue, setComposerValue] = useState("");

  // Re-sync from appState only when the ACTIVE album changes (not on every
  // unrelated appState notification) - matches the original's behavior of
  // never auto-refreshing comments from the background poll, only on
  // explicit navigation or a CRUD action's own re-render call.
  useEffect(() => {
    setComments(album?.comments || []);
    setOpenReplyForId(null);
    setOpenEditForId(null);
  }, [albumsDetailId]);

  function writeThrough(next: AlbumComment[]) {
    setComments(next);
    if (album) {
      album.comments = next;
      notifyStateChange();
    }
  }

  async function handlePostComment(e: React.FormEvent) {
    e.preventDefault();
    const text = composerValue.trim();
    if (!text || !albumsDetailId) return;

    try {
      const res = await apiFetch(`/api/albums/${albumsDetailId}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });

      if (res.status === 401) {
        requestLock("unauthorized");
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        showToast(data.message || "Failed to post comment.", "error");
        return;
      }

      const data = await res.json();
      writeThrough([...comments, data.comment]);
      setComposerValue("");
    } catch {
      showToast("Failed to post comment.", "error");
    }
  }

  async function handleLike(commentId: string) {
    if (!albumsDetailId) return;
    try {
      const res = await apiFetch(`/api/albums/${albumsDetailId}/comments/${commentId}/like`, { method: "POST" });
      if (res.status === 401) {
        requestLock("unauthorized");
        return;
      }
      if (!res.ok) {
        showToast("Failed to like comment.", "error");
        return;
      }
      const data = await res.json();
      writeThrough(comments.map((c) => (c.id === commentId ? { ...c, likes: data.likes } : c)));
    } catch {
      showToast("Failed to like comment.", "error");
    }
  }

  async function handleDelete(commentId: string) {
    if (!albumsDetailId) return;
    const ok = await confirmDialog({ title: "Delete Comment", message: "Delete this comment?", confirmLabel: "Delete" });
    if (!ok) return;

    try {
      const res = await apiFetch(`/api/albums/${albumsDetailId}/comments/${commentId}`, { method: "DELETE" });
      if (res.status === 401) {
        requestLock("unauthorized");
        return;
      }
      if (!res.ok) {
        showToast("Failed to delete comment.", "error");
        return;
      }
      writeThrough(comments.filter((c) => c.id !== commentId && c.parentId !== commentId));
      if (openReplyForId === commentId) setOpenReplyForId(null);
      if (openEditForId === commentId) setOpenEditForId(null);
    } catch {
      showToast("Failed to delete comment.", "error");
    }
  }

  async function handleSubmitReply(parentId: string, text: string) {
    if (!albumsDetailId) return;
    try {
      const res = await apiFetch(`/api/albums/${albumsDetailId}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, parentId }),
      });
      if (res.status === 401) {
        requestLock("unauthorized");
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        showToast(data.message || "Failed to post reply.", "error");
        return;
      }
      const data = await res.json();
      writeThrough([...comments, data.comment]);
      setOpenReplyForId(null);
    } catch {
      showToast("Failed to post reply.", "error");
    }
  }

  async function handleSubmitEdit(commentId: string, text: string) {
    if (!albumsDetailId) return;
    try {
      const res = await apiFetch(`/api/albums/${albumsDetailId}/comments/${commentId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
      if (res.status === 401) {
        requestLock("unauthorized");
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        showToast(data.message || "Failed to save comment.", "error");
        return;
      }
      const data = await res.json();
      writeThrough(
        comments.map((c) => (c.id === commentId ? { ...c, text: data.comment.text, updatedAt: data.comment.updatedAt } : c)),
      );
      setOpenEditForId(null);
    } catch {
      showToast("Failed to save comment.", "error");
    }
  }

  const topLevel = comments.filter((c) => !c.parentId);

  return (
    <>
      <h3 className="h6 mb-3">
        <i className="bi bi-chat-left-text me-2 text-pink" aria-hidden="true" />
        Comments
      </h3>
      <div className="album-comments-list" role="list" aria-label="Album comments">
        {!comments.length && <p className="small muted mb-0">No comments yet. Be the first to say something.</p>}
        {topLevel.map((c) => (
          <Fragment key={c.id}>
            <CommentItem
              key={c.id}
              comment={c}
              isReply={false}
              isOwn={!!currentUserEmail && c.authorEmail === currentUserEmail}
              liked={!!currentUserEmail && (c.likes || []).includes(currentUserEmail)}
              isEditing={openEditForId === c.id}
              isReplying={openReplyForId === c.id}
              onLike={() => handleLike(c.id)}
              onToggleReply={() => {
                setOpenReplyForId(openReplyForId === c.id ? null : c.id);
                setOpenEditForId(null);
              }}
              onToggleEdit={() => {
                setOpenEditForId(openEditForId === c.id ? null : c.id);
                setOpenReplyForId(null);
              }}
              onDelete={() => handleDelete(c.id)}
              onSubmitReply={(text) => handleSubmitReply(c.id, text)}
              onSubmitEdit={(text) => handleSubmitEdit(c.id, text)}
            />
            {comments
              .filter((r) => r.parentId === c.id)
              .map((r) => (
                <CommentItem
                  key={r.id}
                  comment={r}
                  isReply
                  isOwn={!!currentUserEmail && r.authorEmail === currentUserEmail}
                  liked={!!currentUserEmail && (r.likes || []).includes(currentUserEmail)}
                  isEditing={openEditForId === r.id}
                  isReplying={openReplyForId === r.id}
                  onLike={() => handleLike(r.id)}
                  onToggleReply={() => {
                    setOpenReplyForId(openReplyForId === r.id ? null : r.id);
                    setOpenEditForId(null);
                  }}
                  onToggleEdit={() => {
                    setOpenEditForId(openEditForId === r.id ? null : r.id);
                    setOpenReplyForId(null);
                  }}
                  onDelete={() => handleDelete(r.id)}
                  onSubmitReply={(text) => handleSubmitReply(c.id, text)}
                  onSubmitEdit={(text) => handleSubmitEdit(r.id, text)}
                />
              ))}
          </Fragment>
        ))}
      </div>
      <form className="album-comment-form d-flex gap-2 mt-3" onSubmit={handlePostComment}>
        <MentionInput
          value={composerValue}
          onChange={setComposerValue}
          placeholder="Add a comment..."
          maxLength={1000}
          className="form-control"
        />
        <button
          type="submit"
          className="btn btn-pink btn-sm flex-shrink-0 d-inline-flex align-items-center justify-content-center"
          aria-label="Post comment"
          title="Post comment"
        >
          <i className="bi bi-send-fill" aria-hidden="true" />
        </button>
      </form>
    </>
  );
}

// Portals into the existing #albumComments container - albums.ts still
// owns toggling its d-none class on navigation; this only owns the
// contents (previously #albumCommentsList + #albumCommentForm's static
// markup, now removed from index.html).
export function CommentThread() {
  const container = getVanillaEl<HTMLDivElement>("albumComments");
  if (!container) return null;
  return createPortal(<CommentsBody />, container);
}
